import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  EyeOutlined,
  LeftOutlined,
  PictureOutlined,
  RightOutlined,
} from "@ant-design/icons";

interface ProductCardMediaProps {
  children?: React.ReactNode;
  productName: string;
  primaryImage?: string | null;
  galleryImages?: string[] | null;
  className: string;
  placeholderClassName: string;
  onOpen: () => void;
}

const SWIPE_THRESHOLD = 36;

const normalizeImages = (
  primaryImage?: string | null,
  galleryImages?: string[] | null
) => {
  // La galerie contient généralement les photos préparées pour la boutique.
  // L'image principale reste disponible en repli, comme dans la fiche produit.
  const candidates = [...(galleryImages || []), primaryImage];

  return candidates.reduce<string[]>((images, candidate) => {
    const normalized = typeof candidate === "string" ? candidate.trim() : "";
    if (normalized && !images.includes(normalized)) {
      images.push(normalized);
    }
    return images;
  }, []);
};

const ProductCardMedia: React.FC<ProductCardMediaProps> = ({
  children,
  productName,
  primaryImage,
  galleryImages,
  className,
  placeholderClassName,
  onOpen,
}) => {
  const images = useMemo(
    () => normalizeImages(primaryImage, galleryImages),
    [primaryImage, galleryImages]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const didSwipe = useRef(false);
  const swipeResetTimer = useRef<number | null>(null);

  const currentImage = images[activeIndex];
  const hasGallery = images.length > 1;

  useEffect(() => {
    setActiveIndex(0);
    setImageLoaded(false);
    setImageError(false);
  }, [primaryImage, galleryImages]);

  useEffect(
    () => () => {
      if (swipeResetTimer.current !== null) {
        window.clearTimeout(swipeResetTimer.current);
      }
    },
    []
  );

  const showImage = (nextIndex: number) => {
    setImageLoaded(false);
    setImageError(false);
    setActiveIndex((nextIndex + images.length) % images.length);
  };

  const showPreviousImage = () => showImage(activeIndex - 1);
  const showNextImage = () => showImage(activeIndex + 1);

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
    didSwipe.current = false;
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!hasGallery || touchStartX.current === null) {
      touchStartX.current = null;
      return;
    }

    const endX = event.changedTouches[0]?.clientX;
    if (typeof endX !== "number") return;

    const deltaX = endX - touchStartX.current;
    touchStartX.current = null;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

    didSwipe.current = true;
    if (deltaX > 0) {
      showPreviousImage();
    } else {
      showNextImage();
    }

    if (swipeResetTimer.current !== null) {
      window.clearTimeout(swipeResetTimer.current);
    }
    swipeResetTimer.current = window.setTimeout(() => {
      didSwipe.current = false;
    }, 450);
  };

  const handleMediaClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (didSwipe.current) {
      didSwipe.current = false;
      return;
    }
    onOpen();
  };

  return (
    <div
      className={className}
      role="group"
      aria-label={`Photos de ${productName}`}
      onClick={handleMediaClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => {
        touchStartX.current = null;
        didSwipe.current = false;
      }}
    >
      {currentImage ? (
        <>
          {!imageLoaded && !imageError && (
            <div className="public-store__image-skeleton" aria-hidden="true" />
          )}
          {imageError ? (
            <div className={placeholderClassName}>
              <PictureOutlined aria-hidden="true" />
              <span>Image non disponible</span>
            </div>
          ) : (
            <img
              key={currentImage}
              className={`public-store__product-image ${
                imageLoaded ? "public-store__product-image--loaded" : ""
              }`}
              src={currentImage}
              alt={`${productName} — photo ${activeIndex + 1} sur ${images.length}`}
              loading="lazy"
              decoding="async"
              draggable={false}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          )}
        </>
      ) : (
        <div className={placeholderClassName}>
          <PictureOutlined aria-hidden="true" />
          <span>Pas d'image</span>
        </div>
      )}

      {children}

      {hasGallery && (
        <>
          <span className="public-store__image-count" aria-live="polite">
            {activeIndex + 1}/{images.length}
          </span>
          <button
            type="button"
            className="public-store__image-nav public-store__image-nav--previous"
            aria-label={`Photo précédente de ${productName}`}
            onClick={(event) => {
              event.stopPropagation();
              showPreviousImage();
            }}
          >
            <LeftOutlined aria-hidden="true" />
          </button>
          <button
            type="button"
            className="public-store__image-nav public-store__image-nav--next"
            aria-label={`Photo suivante de ${productName}`}
            onClick={(event) => {
              event.stopPropagation();
              showNextImage();
            }}
          >
            <RightOutlined aria-hidden="true" />
          </button>
        </>
      )}

      <button
        type="button"
        className="public-store__image-open"
        aria-label={`Voir les détails de ${productName}`}
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        <EyeOutlined aria-hidden="true" />
        <span>Voir le produit</span>
      </button>
    </div>
  );
};

export default ProductCardMedia;
